import React from "react";
import { useAnketaI18n } from "src/internationalization";

/*
 * "Why opsanio" - the three sovereignty pillars (Souverän by Design /
 * Vom Asset zur Kontrolle / KI ohne Kontrollverlust). On the navy canvas to
 * echo the hero and carry the emotional differentiator. Copy is verbatim from
 * opsanio's elevator pitch, translated for both locales.
 */

const tPrefix = "anketa.autonubil.home.why";

type PillarIcon = "shield" | "layers" | "ai";

const Icon: React.FC<{ kind: PillarIcon }> = ({ kind }) => {
  const c = {
    width: 28,
    height: 28,
    viewBox: "0 0 24 24",
    fill: "none",
    "aria-hidden": true as const,
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  if (kind === "shield")
    return (
      <svg {...c}>
        <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    );
  if (kind === "layers")
    return (
      <svg {...c}>
        <path d="M12 3l9 5-9 5-9-5 9-5z" />
        <path d="M3 13l9 5 9-5" />
      </svg>
    );
  return (
    <svg {...c}>
      <rect x="5" y="8" width="14" height="11" rx="2" />
      <path d="M12 8V5M9 5h6M9 13h.01M15 13h.01M9.5 16h5" />
    </svg>
  );
};

const PILLARS: { key: string; icon: PillarIcon; titleFb: string; descFb: string }[] = [
  {
    key: "sovereign",
    icon: "shield",
    titleFb: "Sovereign by design",
    descFb:
      "Operated on-premises by us - your data stays physically in-house. Not a promise, but architecture.",
  },
  {
    key: "control",
    icon: "layers",
    titleFb: "From asset to control",
    descFb:
      "The only layer that unifies asset data, SIEM and automatic GRC / NIS2 mapping as one operated service.",
  },
  {
    key: "ai",
    icon: "ai",
    titleFb: "AI without losing control",
    descFb:
      "Tarnkappe makes AI usable on sensitive security data - re-identifiable data never leaves your network.",
  },
];

export const AutonubilWhyOpsanio = (): JSX.Element => {
  const { translate } = useAnketaI18n();

  return (
    <section
      style={{
        position: "relative",
        overflow: "hidden",
        isolation: "isolate",
        color: "var(--ak-on-navy)",
        background: "var(--ak-hero-bg)",
        padding: "clamp(64px, 9vw, 96px) clamp(24px, 5vw, 48px)",
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
          backgroundImage:
            "linear-gradient(var(--ak-hero-grid) 1px, transparent 1px), linear-gradient(90deg, var(--ak-hero-grid) 1px, transparent 1px)",
          backgroundSize: "52px 52px",
          maskImage: "radial-gradient(100% 80% at 50% 0%, #000 35%, transparent 80%)",
          WebkitMaskImage: "radial-gradient(100% 80% at 50% 0%, #000 35%, transparent 80%)",
        }}
      />
      <div style={{ position: "relative", zIndex: 1, maxWidth: 1160, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <div
            style={{
              fontSize: "0.7rem",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "var(--ak-accent-2)",
              marginBottom: 12,
            }}
          >
            {translate(`${tPrefix}.eyebrow`, { defaultMessage: "Why opsanio" } as any)}
          </div>
          <h2
            style={{
              color: "var(--ak-on-navy)",
              fontWeight: 800,
              letterSpacing: "-0.025em",
              fontSize: "clamp(1.9rem, 4vw, 2.8rem)",
              margin: 0,
              textWrap: "balance",
            }}
          >
            {translate(`${tPrefix}.title`, { defaultMessage: "Sovereign. Integrated. AI-ready." } as any)}
          </h2>
          <p
            style={{
              maxWidth: 640,
              margin: "16px auto 0",
              color: "var(--ak-on-navy-secondary)",
              fontSize: "clamp(0.97rem, 1.6vw, 1.15rem)",
              lineHeight: 1.65,
            }}
          >
            {translate(`${tPrefix}.subtitle`, {
              defaultMessage: "As IT grows, it has to get simpler - clarity instead of chaos, without dependence on global players.",
            } as any)}
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 20,
          }}
        >
          {PILLARS.map((p) => (
            <div
              key={p.key}
              style={{
                background: "var(--ak-on-navy-surface)",
                border: "1px solid var(--ak-on-navy-border)",
                borderRadius: 16,
                padding: "30px 26px",
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 14,
                  background: "var(--ak-on-navy-surface-2)",
                  border: "1px solid var(--ak-accent-border)",
                  color: "var(--ak-accent-2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 20,
                }}
              >
                <Icon kind={p.icon} />
              </div>
              <h3
                style={{
                  margin: "0 0 10px",
                  fontSize: "1.2rem",
                  fontWeight: 700,
                  letterSpacing: "-0.01em",
                  color: "var(--ak-on-navy)",
                }}
              >
                {translate(`${tPrefix}.${p.key}.title`, { defaultMessage: p.titleFb } as any)}
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: "0.95rem",
                  lineHeight: 1.65,
                  color: "var(--ak-on-navy-secondary)",
                }}
              >
                {translate(`${tPrefix}.${p.key}.desc`, { defaultMessage: p.descFb } as any)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AutonubilWhyOpsanio;
